import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from './useSnackbar';
import useDealer from './useDealer';
import PopularModelsService from 'services/PopularModelsService';
import { safeParseList } from 'helpers';


const vehicleTitleSettingsOpt = [
    { value: 'MNO', label: 'Model Name Only' },
    { value: 'MP', label: 'Model + Parts' },
    { value: 'MA', label: 'Model + Accessories' },
    { value: 'MM', label: 'Make + Model' },
    { value: 'MMP', label: 'Make + Model + Parts' },
    { value: 'MMA', label: 'Make + Model + Accessories' },
];

const popularModelDestinationOpt = [
    { value: 'P', label: 'Parts' },
    { value: 'A', label: 'Accessories' },
];

const normalizePopularModelSelection = (value) => {
    const parsedValue = safeParseList(value);

    if (!Array.isArray(parsedValue)) {
        return [];
    }

    return parsedValue.map((item) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
            return {
                modelId: String(item.modelId),
                appearances: Array.isArray(item.appearances) ? item.appearances : [],
            };
        }

        return {
            modelId: String(item),
            appearances: [],
        };
    }).filter((item) => item.modelId && item.modelId !== 'undefined');
};

const buildAppearanceMap = (selection = []) =>
    selection.reduce((acc, item) => {
        acc[item.modelId] = Array.isArray(item.appearances) ? item.appearances : [];
        return acc;
    }, {});

const normalizeSelectionPayload = (selectedModels = [], selectedAppearancesByModel = {}) =>
    selectedModels.map((modelId) => ({
        modelId: String(modelId),
        appearances: [...(selectedAppearancesByModel[String(modelId)] || [])]
            .map((appearance) => ({
                id: String(appearance.id),
                code: appearance.code || '',
                image: appearance.image || '',
            }))
            .sort((left, right) => left.id.localeCompare(right.id)),
    }));

const usePopularModels = () => {
    const dealer = useDealer();
    const [initialValues, setInitialValues] = useState({
        popularModelsTitleFormat: '',
        popularModelsHeading: '',
        popularModelsDestination: '',
        popularModelsSubtitle: '',
    });
    const [initialSelectedModels, setInitialSelectedModels] = useState([]);
    const [initialSelectedAppearancesByModel, setInitialSelectedAppearancesByModel] = useState({});
    const [allPopularModels, setAllPopularModels] = useState([]);
    const [selectedModels, setSelectedModels] = useState([]);
    const [selectedAppearancesByModel, setSelectedAppearancesByModel] = useState({});
    const openSnackbar = useSnackbar();
    const validationSchema = yup.object({

    });

    useEffect(() => {
        const fetchPopularModels = async (dealerId) => {
            try {
                const data = await PopularModelsService.getAll(dealerId);
                setAllPopularModels(data);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        if (dealer.id) {
            fetchPopularModels(dealer.id);
            const parsedSelection = normalizePopularModelSelection(dealer.selectedPopularModels);
            const parsedSelectedModels = parsedSelection.map((item) => item.modelId);
            const parsedSelectedAppearancesByModel = buildAppearanceMap(parsedSelection);
            setInitialValues({
                popularModelsTitleFormat: dealer.popularModelsTitleFormat,
                popularModelsHeading: dealer.popularModelsHeading,
                popularModelsDestination: dealer.popularModelsDestination,
                popularModelsSubtitle: dealer.popularModelsSubtitle
            });
            setInitialSelectedModels(parsedSelectedModels);
            setInitialSelectedAppearancesByModel(parsedSelectedAppearancesByModel);
            setSelectedModels(parsedSelectedModels);
            setSelectedAppearancesByModel(parsedSelectedAppearancesByModel);

        }
    }, [dealer]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            updatePopularModels(values);
        },
    });

    const updatePopularModels = async (values) => {
        try {
            const savedSelectionPayload = normalizeSelectionPayload(
                selectedModels,
                selectedAppearancesByModel,
            );
            const savedSelectedModels = savedSelectionPayload.map((item) => item.modelId);
            const savedSelectedAppearancesByModel = buildAppearanceMap(savedSelectionPayload);
            const savedFormValues = {
                popularModelsTitleFormat: values.popularModelsTitleFormat || '',
                popularModelsHeading: values.popularModelsHeading || '',
                popularModelsDestination: values.popularModelsDestination || '',
                popularModelsSubtitle: values.popularModelsSubtitle || '',
            };

            await PopularModelsService.put({
                ...savedFormValues,
                selectedModels: savedSelectionPayload,
                dealerId: dealer.id,
            });
            setInitialValues(savedFormValues);
            setInitialSelectedModels(savedSelectedModels);
            setInitialSelectedAppearancesByModel(savedSelectedAppearancesByModel);
            formik.resetForm({ values: savedFormValues });
            openSnackbar('success', 'Updated successfully.');
        } catch (error) {
            openSnackbar('error', 'An error occurred while updating. Please try again.');
        }
    }

    const isModelSelected = (id) => selectedModels.some((selectedId) => String(selectedId) === String(id));

    const handleClickCheckbox = id => {
        if (isModelSelected(id)) {
            const updatedSelected = [...selectedModels].filter((selectedId) => String(selectedId) !== String(id));
            setSelectedModels(updatedSelected);
            setSelectedAppearancesByModel((current) => {
                const nextValue = { ...current };
                delete nextValue[String(id)];
                return nextValue;
            });
        } else {
            setSelectedModels([...selectedModels, id]);
        }
    }

    const toggleAppearance = (modelId, appearance) => {
        setSelectedAppearancesByModel((current) => {
            const key = String(modelId);
            const currentAppearances = current[key] || [];
            const alreadySelected = currentAppearances.some(
                (item) => String(item.id) === String(appearance.id),
            );

            return {
                ...current,
                [key]: alreadySelected
                    ? currentAppearances.filter((item) => String(item.id) !== String(appearance.id))
                    : [...currentAppearances, appearance],
            };
        });
    };

    const isAppearanceSelected = (modelId, appearanceId) =>
        (selectedAppearancesByModel[String(modelId)] || []).some(
            (item) => String(item.id) === String(appearanceId),
        );

    const clearSelectedModels = () => {
        setSelectedModels([]);
        setSelectedAppearancesByModel({});
    };

    const resetPopularModels = () => {
        formik.resetForm();
        setSelectedModels(initialSelectedModels);
        setSelectedAppearancesByModel(initialSelectedAppearancesByModel);
    };

    const currentSelectionPayload = normalizeSelectionPayload(
        selectedModels,
        selectedAppearancesByModel,
    );
    const initialSelectionPayload = normalizeSelectionPayload(
        initialSelectedModels,
        initialSelectedAppearancesByModel,
    );
    const hasUnsavedChanges =
        formik.dirty ||
        JSON.stringify(currentSelectionPayload) !== JSON.stringify(initialSelectionPayload);

    return {
        dealer,
        selectedModels,
        selectedAppearancesByModel,
        vehicleTitleSettingsOpt,
        popularModelDestinationOpt,
        allPopularModels,
        formik,
        handleClickCheckbox,
        toggleAppearance,
        isModelSelected,
        isAppearanceSelected,
        clearSelectedModels,
        resetPopularModels,
        hasUnsavedChanges
    }
}

export default usePopularModels;
